"use client";

/**
 * Client-side Application Insights registry.
 *
 * The SDK is loaded and registered only by src/instrumentation-client.ts, so
 * components that report telemetry (error boundaries, etc.) stay SDK-free —
 * the type-only import below is erased at compile time. All helpers are
 * no-ops until the SDK registers, or forever when the connection string
 * isn't configured.
 */
import type { ApplicationInsights } from "@microsoft/applicationinsights-web";

let appInsights: ApplicationInsights | null = null;

export function registerAppInsights(instance: ApplicationInsights) {
  appInsights = instance;
}

export function trackPageView(uri?: string) {
  try {
    appInsights?.trackPageView(uri ? { uri } : undefined);
  } catch {
    /* telemetry must never break the app */
  }
}

/**
 * Report an error caught by a React error boundary.
 *
 * `props` carries whatever the boundary knows that the error itself doesn't —
 * most usefully `recovered`, which separates the stale-chunk errors we
 * reload through from the ones a visitor actually saw as a crash screen.
 */
export function reportClientError(
  error: Error & { digest?: string },
  boundary: string,
  props?: Record<string, string>,
) {
  try {
    appInsights?.trackException({
      exception: error,
      properties: { boundary, digest: error.digest ?? "", ...props },
    });
  } catch {
    /* ignore */
  }
}
